import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useAuthor } from '@/hooks/useAuthor';
import { getShowcasePubkey, NAV_LINKS } from '@/lib/showcase';
import { getDisplayName } from '@/lib/getDisplayName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { ThemeToggle } from '@/components/ThemeToggle';
import { cn } from '@/lib/utils';

export function SiteHeader() {
  const pubkey = getShowcasePubkey();
  const author = useAuthor(pubkey);
  const [menuOpen, setMenuOpen] = useState(false);

  const metadata = author.data?.metadata;
  const title =
    metadata?.display_name || getDisplayName(metadata, pubkey);
  const banner = metadata?.banner ? sanitizeUrl(metadata.banner) : undefined;
  const logo = metadata?.picture ? sanitizeUrl(metadata.picture) : undefined;

  return (
    <header className="w-full">
      {/* Hero banner */}
      <div className="relative isolate w-full overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-primary">
          {banner && (
            <img
              src={banner}
              alt=""
              className="h-full w-full object-cover opacity-70"
              loading="eager"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/40" />
        </div>

        <div className="mx-auto flex max-w-5xl flex-col items-center px-4 py-14 sm:py-20 text-center">
          <Link to="/" className="group inline-flex flex-col items-center gap-4">
            {logo && (
              <img
                src={logo}
                alt={title}
                className="size-20 sm:size-24 rounded-full border-4 border-white/80 object-cover shadow-lg"
              />
            )}
            <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white drop-shadow-sm">
              {title}
            </h1>
          </Link>
          {metadata?.about && (
            <p className="mt-4 max-w-2xl text-sm sm:text-base leading-relaxed text-white/90">
              {metadata.about}
            </p>
          )}
        </div>
      </div>

      {/* Nav bar */}
      <div className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur-md">
        <nav className="mx-auto flex max-w-5xl items-center justify-between px-4 h-14">
          <div className="hidden sm:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <NavItem key={link.label} {...link} />
            ))}
          </div>

          {/* Mobile toggle */}
          <button
            className="sm:hidden inline-flex items-center gap-2 text-sm font-medium"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            Menu
          </button>

          <ThemeToggle />
        </nav>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="sm:hidden border-t border-border px-4 py-2">
            <div className="flex flex-col">
              {NAV_LINKS.map((link) => (
                <NavItem
                  key={link.label}
                  {...link}
                  className="py-2.5"
                  onClick={() => setMenuOpen(false)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function NavItem({
  label,
  href,
  external,
  variant = 'link',
  className,
  onClick,
}: {
  label: string;
  href: string;
  external?: boolean;
  variant?: 'link' | 'button';
  className?: string;
  onClick?: () => void;
}) {
  const base = cn(
    'text-sm font-medium uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    variant === 'button'
      ? 'inline-flex items-center rounded-full bg-primary px-4 py-1.5 text-primary-foreground shadow-sm hover:bg-primary/90'
      : 'rounded-md px-3 py-2 text-foreground/80 hover:text-primary',
    className,
  );

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={base}
        onClick={onClick}
      >
        {label}
      </a>
    );
  }

  return (
    <Link to={href} className={base} onClick={onClick}>
      {label}
    </Link>
  );
}
